import JSZip from 'jszip';
import { loadDirectGlbFile } from './glbLoader';

export const detectFileType = (url) => {
    const lowerUrl = url.toLowerCase();
    if (lowerUrl.endsWith('.glb')) {
        return 'glb';
    } else if (lowerUrl.endsWith('.obj')) {
        return 'obj';
    } else if (lowerUrl.endsWith('.zip')) {
        return 'zip';
    }
    return null;
};

export const extractModelFromZip = async (zipUrl) => {
    try {
        const response = await fetch(zipUrl);
        if (!response.ok) {
            throw new Error('Failed to fetch ZIP file');
        }
        
        const zipBlob = await response.blob();
        const zip = new JSZip();
        let zipContent;
        
        try {
            zipContent = await zip.loadAsync(zipBlob);
        } catch (zipError) {
            throw new Error('Failed to load ZIP file');
        }
        
        const glbFiles = [];
        const objFiles = [];
        const mtlFiles = [];
        const textures = {};
        
        for (const [filename, file] of Object.entries(zipContent.files)) {
            if (filename.toLowerCase().endsWith('.glb')) {
                glbFiles.push(filename);
            } else if (filename.toLowerCase().endsWith('.obj')) {
                objFiles.push(filename);
            } else if (filename.toLowerCase().endsWith('.mtl')) {
                mtlFiles.push(filename);
            } else if (/\.(jpe?g|png|tga|bmp)$/i.test(filename)) {
                textures[filename] = await file.async('blob');
            }
        }
        
        if (glbFiles.length > 0) {
            const glbFileName = glbFiles[0];
            const glbBlob = await zipContent.file(glbFileName).async('blob');
            const glbUrl = URL.createObjectURL(glbBlob);
            
            return {
                type: 'glb',
                url: glbUrl,
                fileName: glbFileName,
                blob: glbBlob
            };
        } else if (objFiles.length > 0) {
            const objFileName = objFiles[0];
            const objContent = await zipContent.file(objFileName).async('text');
            
            let mtlContent = null;
            const objNameWithoutExt = objFileName.replace(/\.obj$/i, '');
            const correspondingMtl = mtlFiles.find(mtl => 
                mtl.toLowerCase().includes(objNameWithoutExt.toLowerCase())
            );
            
            if (correspondingMtl) {
                mtlContent = await zipContent.file(correspondingMtl).async('text');
            }
            
            return {
                type: 'obj',
                objContent,
                mtlContent,
                fileName: objFileName,
                mtlFileName: correspondingMtl,
                textures
            };
        } else {
            const foundFiles = Object.keys(zipContent.files);
            throw new Error('No GLB or OBJ files found in ZIP');
        }
    } catch (error) {
        console.error('Error extracting model from ZIP:', error);
        throw error;
    }
};

export const loadUnifiedModel = async (url) => {
    try {
        const fileType = detectFileType(url);
        
        if (!fileType) {
            throw new Error('Unsupported file type. Only .glb, .obj, and .zip files are supported.');
        }
        
        if (fileType === 'zip') {
            return await extractModelFromZip(url);
        } else if (fileType === 'glb') {
            const { glbUrl, glbFileName, glbBlob } = await loadDirectGlbFile(url);
            return {
                type: 'glb',
                url: glbUrl,
                fileName: glbFileName,
                blob: glbBlob
            };
        } else if (fileType === 'obj') {
            const response = await fetch(url);
            if (!response.ok) {
                throw new Error('Failed to fetch OBJ file');
            }
            
            const objContent = await response.text();
            
            const baseUrl = url.substring(0, url.lastIndexOf('/') + 1);
            const objFileName = url.substring(url.lastIndexOf('/') + 1);
            const objNameWithoutExt = objFileName.replace(/\.obj$/i, '');
            const mtlUrl = baseUrl + objNameWithoutExt + '.mtl';
            
            let mtlContent = null;
            try {
                const mtlResponse = await fetch(mtlUrl);
                if (mtlResponse.ok) {
                    mtlContent = await mtlResponse.text();
                }
            } catch (mtlError) {
                console.log('No MTL file found, using default materials');
            }
            
            return {
                type: 'obj',
                objContent,
                mtlContent,
                fileName: objFileName,
                mtlFileName: mtlContent ? objNameWithoutExt + '.mtl' : null,
                textures: {}
            };
        }
    } catch (error) {
        console.error('Error loading unified model:', error);
        throw error;
    }
}; 